import { TableReservationItemFragment, TableReservationSourceType } from '@client/graphql/types/graphql.ts';
import { useTranslate } from '@tolgee/react';
import { useParams } from 'react-router-dom';
import useTableReservationUpdateV2 from '@client/page/management/table-reservation/detail/logic/use-update-reservation-v2.ts';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import useFormData from '@client/module/utils/use-form-data.ts';
import { useEffect } from 'react';
import { useCompanyService } from '@packages/core/company/company-hook.ts';
import moment from 'moment-timezone';
import { extractMessageError } from '@client/module/utils/extract-message-error.ts';
import Card from '@packages/ui/card/card.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import { objectIncludeSome } from '@client/module/utils/object-include.ts';
import { CalendarDateRangeIcon } from '@heroicons/react/24/outline';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import PhoneInput from 'react-phone-number-input/min';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';

interface TableBookingUpdateForm {
  platformType: TableReservationSourceType;
  seats: number;
  guestName: string;
  updateMessage: string;
  bookingDate: string;
  bookingTime: string;
  phone: string;
  email: string;
}

export default function ReservationEditModal(props: { onClose: () => void; data: TableReservationItemFragment }) {
  const reservationID = useParams().id ?? '';
  const { t } = useTranslate();
  const notificationCenter = useNotificationCenter();
  const company = useCompanyService();

  const [update] = useTableReservationUpdateV2();

  const {
    data: formData,
    setData,
    manualHandle,
    handle,
  } = useFormData<TableBookingUpdateForm>({
    platformType: TableReservationSourceType.System,
    seats: 0,
    guestName: '',
    updateMessage: '',
    bookingTime: '',
    bookingDate: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    setData({
      email: props.data.email,
      guestName: props.data.guestName,
      updateMessage: '',
      phone: props.data.phone,
      platformType: props.data.platformType,
      seats: props.data.seats,
      bookingDate: moment
        .tz(props.data.startTime, company.company?.settings.timezone ?? 'Europe/Berlin')
        .format('YYYY-MM-DD'),
      bookingTime: moment
        .tz(props.data.startTime, company.company?.settings.timezone ?? 'Europe/Berlin')
        .format('HH:mm'),
    });
  }, [props.data, setData, company.company?.settings.timezone]);

  const updateReservation = async () => {
    try {
      await update({
        variables: {
          id: reservationID,
          input: {
            email: formData.email,
            guestName: formData.guestName,
            updateMessage: formData.updateMessage,
            phone: formData.phone,
            seats: formData.seats,
            startTime: `${formData.bookingDate}T${formData.bookingTime}`,
          },
        },
      });

      notificationCenter.showAlert({
        status: 'success',
        message: t('reservation.edit.alert.message.success'),
        title: t('reservation.edit.alert.title'),
      });

      props.onClose();
    } catch (error) {
      notificationCenter.showAlert({
        status: 'error',
        message: t('reservation.edit.alert.message.error') + extractMessageError(error),
        title: t('reservation.edit.alert.title'),
      });
    }
  };
  return (
    <Card>
      <CardHeader title={t('reservation.edit.title')} />
      <CardContent>
        <FormLayout>
          <div className="grid grid-cols-1 grid-cols-2 gap-x-4 gap-y-8">
            {/* Name */}
            <FormItem className="max-w-md" title={t('common.form-input.name')}>
              <TextInput
                type="text"
                label="guestName"
                value={formData.guestName}
                name="guestName"
                placeholder={t('common.placeholder.guest-name')}
                onChange={handle}
              />
            </FormItem>

            {/* Seat */}
            <FormItem className="max-w-md" title={t('common.form-input.number-of-guests')}>
              <TextInput
                type="number"
                label="seats"
                value={formData.seats}
                name="seats"
                placeholder={t('common.placeholder.number-of-guests')}
                onChange={(value) => {
                  manualHandle('seats', parseInt(value.target.value));
                }}
              />
            </FormItem>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-8">
            {/* Email */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-4 ">
              <FormItem className="w-full " title={t('common.form-input.email')}>
                <TextInput
                  className="max-w-md"
                  type="text"
                  label="email"
                  value={formData.email}
                  name="email"
                  placeholder={t('common.placeholder.guest-email')}
                  onChange={handle}
                />
              </FormItem>
            </div>
            {/*  Phone */}
            <FormItem className="max-w-md" title={t('common.form-input.phone')}>
              <PhoneInput
                className="pl-2.5 pt-1.5 pb-1.5 pr-2 h-auto block w-full max-w-md rounded-md text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300
                  focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                onChange={(value) => {
                  manualHandle('phone', value);
                }}
                value={formData.phone}
                defaultCountry={'DE'}
                placeholder={t('common.form-input.phone')}
              />
            </FormItem>
          </div>

          <div className="flex flex-row items-center gap-x-4 gap-y-8">
            <FormItem className="max-w-md" title={'Booking date'}>
              <input
                className="rounded-md max-w-full border-gray-300"
                aria-label="Booking date"
                id="bookingDate"
                name="bookingDate"
                value={formData.bookingDate}
                type="date"
                onChange={handle}
              />
            </FormItem>

            <FormItem className="max-w-md" title={t('common.booking-time')}>
              <input
                className="rounded-md max-w-full border-gray-300"
                aria-label="Booking time"
                id="bookingTime"
                name="bookingTime"
                value={formData.bookingTime}
                type="time"
                onChange={handle}
              />
            </FormItem>
          </div>

          {objectIncludeSome(props.data, ['flexTime']) && (
            <FormItem title={t('reservation.edit.form-input.flex-time.title')}>
              {props.data.flexTime && (
                <div className="flex flex-row gap-1 items-center">
                  <CalendarDateRangeIcon className="w-5 h-5 stroke-amber-700" />
                  <p>
                    {t({
                      key: 'reservation.edit.form-input.flex-time.description',
                      defaultValue: 'The user can move the reservation up to {minute} minutes later',
                      params: { minute: props.data.flexTime },
                    })}
                  </p>
                </div>
              )}
            </FormItem>
          )}

          {/*  Message */}
          <FormItem className="max-w-md" title={t('common.form-input.message')}>
            <textarea
              id="message"
              name="message"
              rows={3}
              value={formData.updateMessage}
              onChange={(e) => {
                manualHandle('updateMessage', e.target.value);
              }}
              className="block h-40 w-full rounded-md border-0 p-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400
                  focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </FormItem>
        </FormLayout>
      </CardContent>

      <CardFooter>
        <div className="w-full flex items-center justify-end">
          <div onClick={props.onClose} className="flex items-center justify-end gap-x-2">
            <SecondaryButton>{t('common.button.cancel')}</SecondaryButton>

            <PrimaryButton
              onClick={() => {
                void updateReservation();
              }}
            >
              {t('common.button.confirm')}
            </PrimaryButton>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
